import React, { useState } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface ImageGalleryProps {
  images: string[];
}

export const ImageGallery: React.FC<ImageGalleryProps> = ({ images }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState<1 | -1>(1);

  if (!images || images.length === 0) return null;

  const openGallery = (index: number) => {
    setCurrentIndex(index);
    setIsOpen(true);
  };

  // ✅ Make scrolling finite (no wrap-around)
  const nextImage = () => {
    if (currentIndex < images.length - 1) {
      setDirection(1);
      setCurrentIndex((prev) => prev + 1);
    }
  };

  const prevImage = () => {
    if (currentIndex > 0) {
      setDirection(-1);
      setCurrentIndex((prev) => prev - 1);
    }
  };

  // Animation variants
  const variants = {
    enter: (dir: number) => ({
      x: dir > 0 ? 300 : -300,
      opacity: 0,
    }),
    center: { x: 0, opacity: 1 },
    exit: (dir: number) => ({
      x: dir < 0 ? 300 : -300,
      opacity: 0,
    }),
  };

  return (
    <>
      {/* 🏠 Image Grid */}
      <div
        className={`grid gap-4 mb-6 ${
          images.length > 1 ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1 md:grid-cols-2'
        }`}
      >
        <div className="h-80 rounded-lg overflow-hidden">
          <img
            src={images[0]}
            onClick={() => openGallery(0)}
            className="w-full h-full object-cover cursor-pointer"
            alt="house"
          />
        </div>

        {images.length > 1 && (
          <div className="h-80 grid grid-cols-2 md:grid-cols-1 gap-4">
            {images[1] && (
              <div className="rounded-lg overflow-hidden">
                <img
                  src={images[1]}
                  onClick={() => openGallery(1)}
                  className="w-full h-full object-cover cursor-pointer"
                  alt="house"
                />
              </div>
            )}

            {images[2] && (
              <div
                className="rounded-lg overflow-hidden relative cursor-pointer"
                onClick={() => openGallery(2)}
              >
                <img
                  src={images[2]}
                  className="w-full h-full object-cover"
                  alt="house"
                />
                {images.length > 3 && (
                  <div className="w-full h-full bg-[#00000070] absolute top-0 flex items-center justify-center">
                    <span className="text-white text-xl font-medium">
                      +{images.length - 2} More images
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* 🖼️ Modal Gallery */}
      {isOpen && (
        <div className="fixed inset-0 z-150 bg-black/90 flex items-center justify-center">
          {/* Close Button */}
          <button
            onClick={() => setIsOpen(false)}
            className="absolute top-4 right-4 text-white hover:text-gray-300 cursor-pointer"
          >
            <X size={30} />
          </button>

          {/* Prev Button */}
          <button
            onClick={prevImage}
            disabled={currentIndex === 0}
            className={`absolute left-4 text-white cursor-pointer ${
              currentIndex === 0
                ? 'opacity-30 cursor-not-allowed'
                : 'hover:text-gray-300'
            }`}
          >
            <ChevronLeft size={40} />
          </button>

          {/* Animated Image */}
          <div className="max-h-[80vh] flex items-center justify-center">
            <AnimatePresence custom={direction} initial={false} mode="wait">
              <motion.img
                key={currentIndex}
                src={images[currentIndex]}
                alt="gallery"
                custom={direction}
                variants={variants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.4 }}
                className="max-w-full max-h-[80vh] rounded-lg shadow-lg object-contain select-none"
              />
            </AnimatePresence>
          </div>

          {/* Next Button */}
          <button
            onClick={nextImage}
            disabled={currentIndex === images.length - 1}
            className={`absolute right-4 text-white cursor-pointer ${
              currentIndex === images.length - 1
                ? 'opacity-30 cursor-not-allowed'
                : 'hover:text-gray-300'
            }`}
          >
            <ChevronRight size={40} />
          </button>
        </div>
      )}
    </>
  );
};
